import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';

const SecurityConfigTab = () => {
  const [sslConfig, setSslConfig] = useState({
    enabled: true,
    certificate: 'cyberguard-2025.crt',
    expiryDate: '2025-12-31',
    autoRenewal: true
  });

  const [domainConfig, setDomainConfig] = useState({
    primaryDomain: 'cyberguard.gov',
    allowedDomains: ['cyberguard.gov', 'security.gov', 'nist.gov'],
    enforceHttps: true
  });

  const certificates = [
    {
      id: 1,
      name: "Primary SSL Certificate",
      domain: "*.cyberguard.gov",
      issuer: "DigiCert Government CA",
      status: "Valid",
      expiryDate: "2025-12-31",
      daysUntilExpiry: 103
    },
    {
      id: 2,
      name: "API Gateway Certificate",
      domain: "api.cyberguard.gov",
      issuer: "DigiCert Government CA",
      status: "Valid",
      expiryDate: "2025-11-15",
      daysUntilExpiry: 57
    },
    {
      id: 3,
      name: "Backup Certificate",
      domain: "backup.cyberguard.gov",
      issuer: "DigiCert Government CA",
      status: "Expiring Soon",
      expiryDate: "2025-10-01",
      daysUntilExpiry: 12
    }
  ];

  const securityPolicies = [
    {
      id: 1,
      name: "Password Policy",
      description: "Minimum 12 characters, special characters required",
      status: "Enabled",
      lastUpdated: "2025-09-15"
    },
    {
      id: 2,
      name: "Session Timeout",
      description: "Auto logout after 30 minutes of inactivity",
      status: "Enabled",
      lastUpdated: "2025-09-10"
    },
    {
      id: 3,
      name: "IP Whitelist",
      description: "Restrict access to government IP ranges",
      status: "Enabled",
      lastUpdated: "2025-09-18"
    },
    {
      id: 4,
      name: "Two-Factor Authentication",
      description: "Mandatory 2FA for all administrative accounts",
      status: "Enabled",
      lastUpdated: "2025-09-12"
    }
  ];

  const getStatusBadge = (status, daysUntilExpiry = null) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    if (status === 'Valid' && daysUntilExpiry > 30) {
      return `${baseClasses} bg-success text-success-foreground`;
    } else if (status === 'Valid' && daysUntilExpiry <= 30) {
      return `${baseClasses} bg-warning text-warning-foreground`;
    } else if (status === 'Expiring Soon') {
      return `${baseClasses} bg-error text-error-foreground`;
    } else if (status === 'Enabled') {
      return `${baseClasses} bg-success text-success-foreground`;
    }
    return `${baseClasses} bg-muted text-muted-foreground`;
  };

  const handleCertificateRenewal = (certId) => {
    console.log('Renewing certificate:', certId);
  };

  const handlePolicyToggle = (policyId) => {
    console.log('Toggling policy:', policyId);
  };

  return (
    <div className="space-y-8">
      {/* SSL Configuration */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Shield" size={20} className="mr-2" />
              SSL Configuration
            </h3>
            <p className="text-sm text-muted-foreground">Manage SSL certificates and HTTPS settings</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left">
            Refresh Status
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="Primary Domain"
              type="text"
              value={domainConfig?.primaryDomain}
              onChange={(e) => setDomainConfig(prev => ({...prev, primaryDomain: e?.target?.value}))}
            />
            <div className="space-y-2">
              <Checkbox
                label="Enforce HTTPS"
                description="Redirect all HTTP traffic to HTTPS"
                checked={domainConfig?.enforceHttps}
                onChange={(e) => setDomainConfig(prev => ({...prev, enforceHttps: e?.target?.checked}))}
              />
              <Checkbox
                label="Auto-renewal"
                description="Automatically renew certificates before expiry"
                checked={sslConfig?.autoRenewal}
                onChange={(e) => setSslConfig(prev => ({...prev, autoRenewal: e?.target?.checked}))}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Current Certificate</span>
                <span className={getStatusBadge('Valid', 103)}>Valid</span>
              </div>
              <p className="text-sm text-muted-foreground">Expires: December 31, 2025</p>
              <p className="text-xs text-muted-foreground">103 days remaining</p>
            </div>
          </div>
        </div>
      </div>
      {/* Certificate Management */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Certificate" size={20} className="mr-2" />
              Certificate Management
            </h3>
            <p className="text-sm text-muted-foreground">Monitor and manage SSL certificates</p>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left">
            Upload Certificate
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-foreground">Certificate</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Domain</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Issuer</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Expiry</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {certificates?.map((cert) => (
                <tr key={cert?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="FileText" size={16} className="text-muted-foreground" />
                      <span className="font-medium text-foreground">{cert?.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-foreground">{cert?.domain}</td>
                  <td className="p-4 text-sm text-muted-foreground">{cert?.issuer}</td>
                  <td className="p-4">
                    <span className={getStatusBadge(cert?.status, cert?.daysUntilExpiry)}>
                      {cert?.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm text-foreground">{cert?.expiryDate}</p>
                      <p className="text-xs text-muted-foreground">{cert?.daysUntilExpiry} days</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        iconName="RefreshCw"
                        onClick={() => handleCertificateRenewal(cert?.id)}
                      />
                      <Button variant="ghost" size="sm" iconName="Download" />
                      <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Security Policies */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Lock" size={20} className="mr-2" />
              Security Policies
            </h3>
            <p className="text-sm text-muted-foreground">Configure system security policies and rules</p>
          </div>
          <Button variant="outline" iconName="Settings" iconPosition="left">
            Configure Policies
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {securityPolicies?.map((policy) => (
            <div key={policy?.id} className="p-4 border border-border rounded-lg hover:bg-muted/50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{policy?.name}</h4>
                  <p className="text-sm text-muted-foreground">{policy?.description}</p>
                </div>
                <span className={getStatusBadge(policy?.status)}>
                  {policy?.status}
                </span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-muted-foreground">
                  Updated: {policy?.lastUpdated}
                </span>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    iconName="Edit"
                    onClick={() => handlePolicyToggle(policy?.id)}
                  />
                  <Button variant="ghost" size="sm" iconName="Settings" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecurityConfigTab;